import {
    Box,
    Grid,
    Typography,
    Avatar,
    Stack,
    Button,
    Paper,
    Divider,
    CardContent,
    CardMedia,
    CircularProgress,
    Skeleton,
} from "@mui/material";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import OceshaLogo from "../../../assets/ocesha_images/ocesha_logo.png";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import { toast } from "react-toastify";
import { ReactComponent as VerifiedTick } from "../../../assets/podcast_monster/verified-tick.svg";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import HeadphonesOutlinedIcon from "@mui/icons-material/HeadphonesOutlined";
import { isMobile } from "react-device-detect";
import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { useGetGeneratedClipsListingsMutation, useGetGeneratedImageListingsMutation } from "../../../redux/api/promptApi";

const interFont = "'Inter', sans-serif";
const cardBg = "#161b22";
const border = "#21262d";
const primary = "#3b82f6";

// ─── helpers ─────────────────────────────────────────────────────────────────

const stopWords = new Set([
    "the","a","an","and","or","but","in","on","at","to","for","of","with","by",
    "from","is","are","was","were","be","been","how","what","why","when","which",
    "who","that","this","these","those","will","can","do","did","has","have","had",
    "not","its","your","our","their","about","as","into","through","up","out","so",
    "than","then","if","we","you","i","it",
]);

function buildFallbackImg(title: string, seed: string) {
    const kw = title
        .toLowerCase()
        .replace(/[^a-z0-9 ]/g, " ")
        .split(" ")
        .filter((w) => w.length > 3 && !stopWords.has(w))
        .slice(0, 2)
        .join(" ");
    const q = encodeURIComponent(kw || "podcast");
    return `https://loremflickr.com/800/450/${q}?lock=${seed.slice(-4)}`;
}

function toYouTubeEmbed(url: string): string | null {
    if (!url) return null;
    const re = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/;
    const m = url.match(re);
    return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : null;
}

function cleanTitle(raw: string) {
    return (raw || "")
        .replace(/<\/?[^>]+(>|$)/g, "")
        .replace(/"/g, "")
        .replace(/\*\*/g, "");
}

function formatDate(iso: string) {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// ─────────────────────────────────────────────────────────────────────────────

export default function IndividualEpisodePage() {
    const navigate   = useNavigate();
    const location   = useLocation();
    const [searchParams] = useSearchParams();
    const apiUrl     = process.env.REACT_APP_SERVER_ENDPOINT || "";
    const supportUid = process.env.REACT_APP_SUPPORT_USER_ID || "";
    const loggedIn   = localStorage.getItem("logged_in");

    // episode passed via navigate state OR fetched by id
    const episodeFromState: any = location.state?.episode || null;
    const episodeId: string =
        location.state?.episodeId ||
        episodeFromState?._id ||
        episodeFromState?.campaign_id ||
        searchParams.get("episode_id") ||
        localStorage.getItem("episodeId") ||
        "";

    const [episode,        setEpisode]        = useState<any>(episodeFromState);
    const [mentor,         setMentor]          = useState<any>(null);
    const [loadingEpisode, setLoadingEpisode]  = useState(!episodeFromState);
    const [videoOpen,      setVideoOpen]       = useState(false);
    const [clips,          setClips]           = useState<any[]>([]);
    const [loadingClips,   setLoadingClips]    = useState(false);
    const [showAllClips,   setShowAllClips]    = useState(false);
    const [images,         setImages]          = useState<any[]>([]);
    const [loadingImages,  setLoadingImages]   = useState(false);
    const [showAllImages,  setShowAllImages]   = useState(false);

    const [getGeneratedClipsListings]  = useGetGeneratedClipsListingsMutation();
    const [getGeneratedImageListings]  = useGetGeneratedImageListingsMutation();

    const hasFetched = useRef(false);

    // ── fetch if episode not in state ─────────────────────────────────────────
    useEffect(() => {
        if (episodeFromState) {
            loadMentor(episodeFromState);
            loadClipsAndImages(episodeFromState);
            return;
        }
        if (!episodeId || hasFetched.current) return;
        hasFetched.current = true;
        (async () => {
            try {
                setLoadingEpisode(true);
                const res = await axios.get(`${apiUrl}/api/prompts/get_episode/${episodeId}`);
                const ep  = res?.data?.episode || res?.data;
                setEpisode(ep);
                loadMentor(ep);
                loadClipsAndImages(ep);
            } catch (err) {
                console.error("Error fetching episode =>", err);
            } finally {
                setLoadingEpisode(false);
            }
        })();
    }, [episodeId]);

    const loadMentor = async (ep: any) => {
        const email = ep?.user_email || localStorage.getItem("user_email") || "";
        if (!email) return;
        try {
            const res = await axios.get(`${apiUrl}/api/prompts/get_episodes_by_user`, {
                params: { user_email: email, limit: 1 },
            });
            if (res?.data?.mentor) setMentor(res.data.mentor);
        } catch (_) {}
    };

    const loadClipsAndImages = async (ep: any) => {
        const productId = ep?.campaign_id || ep?._id || "";
        const userEmail = ep?.user_email || localStorage.getItem("user_email") || "";
        if (!userEmail) return;

        // Fetch clips using RTK mutation (same as clips_section.tsx)
        setLoadingClips(true);
        try {
            const response = await getGeneratedClipsListings({
                email: userEmail,
                product_id: productId,
                limitval: 800,
                skipval: 0,
                search: "",
            });
            if ("data" in response && Array.isArray(response.data)) {
                setClips(response.data);
            }
        } catch (_) {}
        finally { setLoadingClips(false); }

        // Fetch images using RTK mutation (same as image_listing.tsx)
        setLoadingImages(true);
        try {
            const response = await getGeneratedImageListings({
                email: userEmail,
                product_id: productId,
                limitval: 800,
                skipval: 0,
            });
            if ("data" in response && Array.isArray(response.data)) {
                setImages(response.data);
            }
        } catch (_) {}
        finally { setLoadingImages(false); }
    };

    // ── derived ───────────────────────────────────────────────────────────────
    const epTitle  = cleanTitle(episode?.title || "");
    const seedStr  = String(episode?._id || episode?.campaign_id || "ep");
    const epImg    = episode?.thump_image_path || buildFallbackImg(epTitle, seedStr);
    const videoUrl = episode?.source === "youtube"
        ? episode?.url
        : (episode?.original_file || episode?.url || "");
    const embedSrc = toYouTubeEmbed(videoUrl);

    const creatorName  = mentor?.name  || episode?.user_name  || "";
    const creatorEmail = mentor?.email || episode?.user_email || "";
    const creatorImg   = mentor?.profile_image || "";

    // ── skeleton ──────────────────────────────────────────────────────────────
    const Skel = () => (
        <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                    <Skeleton variant="text"    height={52} width="75%" sx={{ bgcolor: "#21262d" }} />
                    <Skeleton variant="text"    height={20} width="35%" sx={{ bgcolor: "#21262d" }} />
                    <Skeleton variant="rounded" height={380}             sx={{ bgcolor: "#21262d" }} />
                    <Skeleton variant="text"    height={18}              sx={{ bgcolor: "#21262d" }} />
                    <Skeleton variant="text"    height={18}              sx={{ bgcolor: "#21262d" }} />
                    <Skeleton variant="text"    height={18} width="55%" sx={{ bgcolor: "#21262d" }} />
                </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
                <Skeleton variant="rounded" height={420} sx={{ bgcolor: "#21262d" }} />
            </Grid>
        </Grid>
    );

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ══════════ NAVBAR — exact copy from individual_courses_page ══════════ */}
            <div className="container">
                <div className="row">
                    <nav className="navbar navbar-expand-md">
                        <div className="container-fluid">
                            <a className="navbar-brand" href={process.env.REACT_APP_LOGOUT_DOMAIN || "https://ocesha.ai"} style={{ width: "5%" }}>
                                <img src={OceshaLogo} className="img-fluid" alt="" style={{ width: "95%" }} />
                            </a>
                            <button
                                className="navbar-toggler"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target="#navbarScroll"
                                aria-controls="navbarScroll"
                                aria-expanded="false"
                                aria-label="Toggle navigation"
                            >
                                <span className="">
                                    <i className="bi bi-list text-white" />
                                </span>
                            </button>
                            <div className="collapse navbar-collapse" id="navbarScroll">
                                <ul className="navbar-nav me-auto my-2 my-lg-0 navbar-nav-scroll ms-md-5 gap-4">
                                    {/* <li className="nav-item">
                                        <a className="nav-link nav_active" style={{ color: "#4ba5eb", fontWeight: 800 }} href="/">
                                            Home
                                        </a>
                                    </li>
                                    <li className="nav-item" style={{ cursor: "pointer" }} onClick={() => navigate("/contactus")}>
                                        <a className="nav-link nav_simple">Contact</a>
                                    </li>
                                    <li className="nav-item" style={{ cursor: "pointer" }} onClick={() => window.open(`/blogs?userid=${supportUid}`, "_blank")}>
                                        <a className="nav-link nav_simple">Blogs</a>
                                    </li>
                                    <li className="nav-item" style={{ cursor: "pointer" }} onClick={() => navigate("/aboutus")}>
                                        <a className="nav-link nav_simple">About Us</a>
                                    </li>
                                    <li className="nav-item" style={{ cursor: "pointer" }} onClick={() => navigate("/codegenie/creator-directory")}>
                                        <a className="nav-link nav_simple">Creator Directory</a>
                                    </li>
                                    <li className="nav-item" style={{ cursor: "pointer" }} onClick={() => navigate("/codegenie/courses-directory")}>
                                        <a className="nav-link nav_simple" style={{ color: "#4ba5eb", fontWeight: 700 }}>Courses Directory</a>
                                    </li> */}
                                </ul>

                                {loggedIn === "true" ? (
                                    <div className="d-flex gap-4" role="search">
                                        <button
                                            className="button_leniar px-3"
                                            type="submit"
                                            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                                            onClick={() => window.history.back()}
                                        >
                                            <NavigateBeforeIcon /> Go Back
                                        </button>
                                    </div>
                                ) : (
                                    <div className="d-flex gap-4" role="search">
                                        <button
                                            className="btn btn-outline-primary px-3"
                                            onClick={() => {
                                                if (localStorage.getItem("logged_in") === "true") {
                                                    localStorage.setItem("activeItem", "blogs");
                                                    navigate("/codegenie/generated-blogs");
                                                } else {
                                                    navigate("/signin");
                                                }
                                            }}
                                        >
                                            Sign In
                                        </button>
                                        <button
                                            className="button_leniar px-3"
                                            type="submit"
                                            onClick={() => navigate("/signup")}
                                        >
                                            Sign Up
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </nav>
                    <Divider />
                </div>
            </div>

            {/* ══════════ BODY — same wrapper as individual_courses_page ══════════ */}
            <Box className="overlay-container" sx={{ pr: isMobile ? 2 : 10, pl: isMobile ? 2 : 10, minHeight: "100vh" }}>
                <Box
                    sx={{
                        color: "#e5e7eb",
                        px: { xs: 2, md: 6 },
                        py: 4,
                        fontFamily: interFont,
                    }}
                >
                    {loadingEpisode ? (
                        <Skel />
                    ) : !episode ? (
                        <Typography color="#9ca3af" fontFamily={interFont}>
                            Episode not found.
                        </Typography>
                    ) : (
                        <Grid container spacing={4} alignItems="flex-start">

                            {/* ══ LEFT CONTENT ══ */}
                            <Grid item xs={12} md={8}>
                                <Stack spacing={3}>

                                    {/* Title block */}
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Typography
                                            variant="h4"
                                            fontWeight={700}
                                            color="#e5e7eb"
                                            fontFamily={interFont}
                                            letterSpacing="-0.02em"
                                            sx={{ flex: 1 }}
                                        >
                                            {epTitle}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ ml: 2, flexShrink: 0 }}>
                                            <Button
                                                size="small"
                                                startIcon={<ShareIcon sx={{ fontSize: "13px" }} />}
                                                onClick={() => {
                                                    const id = episodeId || episode?._id || episode?.campaign_id || "";
                                                    const shareUrl = `${window.location.origin}/codegenie/episode-detail?episode_id=${id}`;
                                                    window.open(shareUrl, "_blank");
                                                }}
                                                sx={{
                                                    textTransform: "none", background: "#21262d", color: "#e5e7eb",
                                                    borderRadius: "6px", px: 1.5, fontSize: 12, fontFamily: interFont,
                                                    fontWeight: 600, border: "1px solid #30363d", whiteSpace: "nowrap",
                                                    "&:hover": { background: "#30363d" },
                                                }}
                                            >
                                                Share
                                            </Button>
                                            <Button
                                                size="small"
                                                startIcon={<ContentCopyIcon sx={{ fontSize: "13px" }} />}
                                                onClick={() => {
                                                    const id = episodeId || episode?._id || episode?.campaign_id || "";
                                                    const shareUrl = `${window.location.origin}/codegenie/episode-detail?episode_id=${id}`;
                                                    if (navigator.clipboard?.writeText) {
                                                        navigator.clipboard.writeText(shareUrl).then(() => toast.success("Link copied!")).catch(() => {
                                                            const el = document.createElement("textarea");
                                                            el.value = shareUrl; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
                                                            toast.success("Link copied!");
                                                        });
                                                    } else {
                                                        const el = document.createElement("textarea");
                                                        el.value = shareUrl; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
                                                        toast.success("Link copied!");
                                                    }
                                                }}
                                                sx={{
                                                    textTransform: "none", background: "#21262d", color: "#e5e7eb",
                                                    borderRadius: "6px", px: 1.5, fontSize: 12, fontFamily: interFont,
                                                    fontWeight: 600, border: "1px solid #30363d", whiteSpace: "nowrap",
                                                    "&:hover": { background: "#30363d" },
                                                }}
                                            >
                                                Copy Link
                                            </Button>
                                        </Stack>
                                        </Stack>

                                        {creatorName && (
                                            <Typography sx={{ mt: 0.5, color: "#6b7280", fontSize: 14, fontFamily: interFont }}>
                                                By{" "}
                                                <Box component="span" color={primary} sx={{ textDecoration: "underline" }}>
                                                    {creatorName}
                                                </Box>
                                            </Typography>
                                        )}

                                        {/* meta chips — exact same style as course page */}
                                        <Stack direction="row" spacing={1} alignItems="center" mt={4} flexWrap="wrap">
                                            {episode?.source && (
                                                <Box sx={{
                                                    display: "flex", alignItems: "center", gap: 0.5,
                                                    px: 1, py: 0.3,
                                                    border: "1px solid #30363d", borderRadius: "6px", background: "#0d1117",
                                                }}>
                                                    <Typography fontSize={13} color="#9ca3af" fontFamily={interFont} textTransform="capitalize">
                                                        {episode.source}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {episode?.created_at && (
                                                <Box sx={{
                                                    px: 1, py: 0.3,
                                                    border: "1px solid #30363d", borderRadius: "6px", background: "#0d1117",
                                                }}>
                                                    <Typography fontSize={13} color="#9ca3af" fontFamily={interFont}>
                                                        {formatDate(episode.created_at)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>

                                    {/* Description — same card style as course "What's Included" */}
                                    {episode?.description && (
                                        <Typography sx={{ color: "#9ca3af", maxWidth: 720, fontFamily: interFont, lineHeight: 1.7, fontSize: 15 }}>
                                            {episode.description}
                                        </Typography>
                                    )}

                                    {/* ── VIDEO PLAYER — same position as course curriculum box ── */}
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" mb={1}>
                                            <Typography fontWeight={600} fontFamily={interFont} color="#e5e7eb">
                                                Episode Video
                                            </Typography>
                                        </Stack>

                                        <Box
                                            sx={{
                                                width: "100%",
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                                border: `1px solid ${border}`,
                                                background: "#0d1117",
                                                position: "relative",
                                            }}
                                        >
                                            {videoOpen && videoUrl ? (
                                                /* ── playing ── */
                                                embedSrc ? (
                                                    <iframe
                                                        width="100%"
                                                        height={isMobile ? 220 : 420}
                                                        src={embedSrc}
                                                        title={epTitle}
                                                        frameBorder="0"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        allowFullScreen
                                                        style={{ display: "block" }}
                                                    />
                                                ) : (
                                                    <video
                                                        controls
                                                        autoPlay
                                                        src={videoUrl}
                                                        style={{
                                                            width: "100%",
                                                            height: isMobile ? 220 : 420,
                                                            objectFit: "contain",
                                                            background: "#000",
                                                            display: "block",
                                                        }}
                                                    />
                                                )
                                            ) : (
                                                /* ── thumbnail + play overlay ── */
                                                <Box
                                                    sx={{ position: "relative", cursor: videoUrl ? "pointer" : "default" }}
                                                    onClick={() => { if (videoUrl) setVideoOpen(true); }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={epImg}
                                                        onError={(e: any) => {
                                                            const img = e.target as HTMLImageElement;
                                                            if (!img.dataset.fb2) {
                                                                img.dataset.fb2 = "1";
                                                                const n = seedStr.split("").reduce((a: number, c: string) => a + c.charCodeAt(0), 0) % 1000;
                                                                img.src = `https://picsum.photos/id/${n}/800/450`;
                                                            }
                                                        }}
                                                        sx={{
                                                            width: "100%",
                                                            height: isMobile ? 220 : 420,
                                                            objectFit: "cover",
                                                            display: "block",
                                                        }}
                                                        alt={epTitle}
                                                    />
                                                    {videoUrl && (
                                                        <Box sx={{
                                                            position: "absolute", inset: 0,
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            background: "rgba(0,0,0,0.35)",
                                                            transition: "background 0.2s",
                                                            "&:hover": { background: "rgba(0,0,0,0.52)" },
                                                        }}>
                                                            <Box sx={{
                                                                width: 72, height: 72, borderRadius: "50%",
                                                                background: "rgba(255,255,255,0.15)",
                                                                backdropFilter: "blur(6px)",
                                                                border: "2px solid rgba(255,255,255,0.4)",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                            }}>
                                                                <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 44, color: "#fff" }} />
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* ── CLIPS SECTION ── always visible ── */}
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography fontWeight={600} fontFamily={interFont} color="#e5e7eb">
                                                    Clips
                                                </Typography>
                                                <Box sx={{
                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                    minWidth: 22, height: 20, px: 0.75,
                                                    borderRadius: "999px", fontSize: 11, fontWeight: 700,
                                                    background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)",
                                                }}>
                                                    {loadingClips ? "..." : clips.length}
                                                </Box>
                                            </Stack>
                                            {!loadingClips && clips.length > 4 && (
                                                <Typography
                                                    fontSize={13} color={primary} fontFamily={interFont}
                                                    sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                                                    onClick={() => setShowAllClips((prev) => !prev)}
                                                >
                                                    {showAllClips ? "View less" : "View more"}
                                                </Typography>
                                            )}
                                        </Stack>

                                        {loadingClips ? (
                                            <Grid container spacing={2}>
                                                {[0, 1, 2, 3].map((i) => (
                                                    <Grid item xs={6} sm={3} key={i}>
                                                        <Skeleton variant="rounded" height={120} sx={{ bgcolor: "#21262d", borderRadius: "10px" }} />
                                                        <Skeleton variant="text" height={16} width="80%" sx={{ bgcolor: "#21262d", mt: 0.5 }} />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : clips.length === 0 ? (
                                            <Box sx={{
                                                py: 3, textAlign: "center",
                                                border: `1px dashed ${border}`,
                                                borderRadius: "10px",
                                                background: "#0d1117",
                                            }}>
                                                <Typography fontSize={13} color="#4b5563" fontFamily={interFont}>
                                                    No clips available for this episode
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Grid container spacing={2}>
                                                {(showAllClips ? clips : clips.slice(0, 4)).map((clip: any, idx: number) => {
                                                    const clipTitle = clip?.title || `Clip ${idx + 1}`;
                                                    const clipThumb = clip?.thumbnail || "";
                                                    const clipUrl   = clip?.video_path || clip?.media_url || clip?.url || "";
                                                    return (
                                                        <Grid item xs={6} sm={3} key={clip._id || idx}>
                                                            <Box
                                                                sx={{
                                                                    borderRadius: "10px", overflow: "hidden",
                                                                    border: `1px solid ${border}`, background: "#0d1117",
                                                                    cursor: clipUrl ? "pointer" : "default",
                                                                    transition: "box-shadow 0.2s",
                                                                    "&:hover": clipUrl ? { boxShadow: "0 4px 16px rgba(59,130,246,0.2)" } : {},
                                                                }}
                                                                onClick={() => clipUrl && window.open(clipUrl, "_blank")}
                                                            >
                                                                <Box sx={{ position: "relative", width: "100%", height: 110 }}>
                                                                    {clipThumb ? (
                                                                        <Box component="img" src={clipThumb}
                                                                            onError={(e: any) => { e.currentTarget.style.display = "none"; }}
                                                                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                                                            alt={clipTitle}
                                                                        />
                                                                    ) : (
                                                                        <Box sx={{ width: "100%", height: "100%", bgcolor: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                            <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 36, color: "#4b5563" }} />
                                                                        </Box>
                                                                    )}
                                                                    {clipUrl && (
                                                                        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)", opacity: 0, transition: "opacity 0.2s", "&:hover": { opacity: 1 } }}>
                                                                            <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 40, color: "#fff" }} />
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                                <Box sx={{ p: 1 }}>
                                                                    <Typography fontSize={12} fontWeight={500} color="#e5e7eb" fontFamily={interFont}
                                                                        sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}
                                                                    >
                                                                        {clipTitle}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        )}
                                    </Box>

                                    {/* ── IMAGES SECTION ── always visible ── */}
                                    <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography fontWeight={600} fontFamily={interFont} color="#e5e7eb">
                                                    Images
                                                </Typography>
                                                <Box sx={{
                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                    minWidth: 22, height: 20, px: 0.75,
                                                    borderRadius: "999px", fontSize: 11, fontWeight: 700,
                                                    background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)",
                                                }}>
                                                    {loadingImages ? "..." : images.length}
                                                </Box>
                                            </Stack>
                                            {!loadingImages && images.length > 6 && (
                                                <Typography
                                                    fontSize={13} color={primary} fontFamily={interFont}
                                                    sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                                                    onClick={() => setShowAllImages((prev) => !prev)}
                                                >
                                                    {showAllImages ? "View less" : "View more"}
                                                </Typography>
                                            )}
                                        </Stack>

                                        {loadingImages ? (
                                            <Grid container spacing={2}>
                                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                                    <Grid item xs={4} sm={2} key={i}>
                                                        <Skeleton variant="rounded" height={90} sx={{ bgcolor: "#21262d", borderRadius: "8px" }} />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : images.length === 0 ? (
                                            <Box sx={{
                                                py: 3, textAlign: "center",
                                                border: `1px dashed ${border}`,
                                                borderRadius: "10px",
                                                background: "#0d1117",
                                            }}>
                                                <Typography fontSize={13} color="#4b5563" fontFamily={interFont}>
                                                    No images available for this episode
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Grid container spacing={1.5}>
                                                {(showAllImages ? images : images.slice(0, 6)).map((img: any, idx: number) => {
                                                    const imgSrc   = img?.image_path || img?.image_url || img?.url || "";
                                                    const imgTitle = img?.image_title || img?.title || `Image ${idx + 1}`;
                                                    return (
                                                        <Grid item xs={4} sm={2} key={img._id || idx}>
                                                            <Box
                                                                sx={{
                                                                    borderRadius: "8px", overflow: "hidden",
                                                                    border: `1px solid ${border}`, background: "#0d1117",
                                                                    cursor: imgSrc ? "pointer" : "default",
                                                                    transition: "transform 0.2s, box-shadow 0.2s",
                                                                    "&:hover": imgSrc ? { transform: "scale(1.03)", boxShadow: "0 4px 16px rgba(59,130,246,0.2)" } : {},
                                                                }}
                                                                onClick={() => imgSrc && window.open(imgSrc, "_blank")}
                                                                title={imgTitle}
                                                            >
                                                                {imgSrc ? (
                                                                    <Box component="img" src={imgSrc}
                                                                        onError={(e: any) => { e.currentTarget.style.display = "none"; }}
                                                                        sx={{ width: "100%", height: 90, objectFit: "cover", display: "block" }}
                                                                        alt={imgTitle}
                                                                    />
                                                                ) : (
                                                                    <Box sx={{ width: "100%", height: 90, bgcolor: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                        <Typography fontSize={18} color="#4b5563">🖼</Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        )}
                                    </Box>

                                </Stack>
                            </Grid>

                            {/* ══ RIGHT SIDEBAR — exact same structure as course page ══ */}
                            <Grid item xs={12} md={4} sx={{ alignSelf: "flex-start" }}>
                                <Box sx={{ position: "sticky", top: 24 }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            background: cardBg,
                                            border: `1px solid ${border}`,
                                            borderRadius: "14px",
                                            overflow: "hidden",
                                            transition: "box-shadow 0.2s",
                                            "&:hover": { boxShadow: "0 4px 20px rgba(59,130,246,0.15)" },
                                        }}
                                    >
                                        {/* Thumbnail — same as CardMedia in course page */}
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={epImg}
                                            onError={(e: any) => {
                                                const img = e.target as HTMLImageElement;
                                                if (!img.dataset.fb2) {
                                                    img.dataset.fb2 = "1";
                                                    const n = seedStr.split("").reduce((a: number, c: string) => a + c.charCodeAt(0), 0) % 1000;
                                                    img.src = `https://picsum.photos/id/${n}/600/320`;
                                                }
                                            }}
                                            sx={{ paddingLeft: "10px", paddingRight: "10px", paddingTop: "10px", borderRadius: 5 }}
                                        />

                                        <CardContent>
                                            <Stack spacing={2}>

                                                {/* Watch Episode button — same layout as Buy Now row */}
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        disableElevation
                                                        startIcon={<PlayCircleOutlineOutlinedIcon />}
                                                        disabled={!videoUrl}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontFamily: interFont,
                                                            fontWeight: 600,
                                                            background: "#7C3AED",
                                                            py: 1.2,
                                                            color: "#ffffff",
                                                            minHeight: 44,
                                                            borderRadius: "8px",
                                                            "&:hover": { background: "#6D28D9" },
                                                        }}
                                                        onClick={() => setVideoOpen(true)}
                                                    >
                                                        Watch Episode
                                                    </Button>
                                                </Stack>

                                                <Divider sx={{ borderColor: "#21262d" }} />

                                                {/* This Episode Includes — same list style as course */}
                                                <Typography fontWeight={700} color="#e5e7eb" fontFamily={interFont} fontSize={14}>
                                                    This Episode Includes
                                                </Typography>

                                                <Stack spacing={1.2}>
                                                    {[
                                                        { icon: <PlayCircleOutlineOutlinedIcon fontSize="small" />, label: "Full episode video" },
                                                        { icon: <HeadphonesOutlinedIcon fontSize="small" />,       label: "Audio podcast"        },
                                                    ].map((item, index) => (
                                                        <Stack key={index} direction="row" spacing={1.2} alignItems="center">
                                                            <Box sx={{
                                                                minWidth: 20,
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                color: primary,
                                                            }}>
                                                                {item.icon}
                                                            </Box>
                                                            <Typography fontSize={13} fontFamily={interFont} sx={{ color: "#9ca3af" }}>
                                                                {item.label}
                                                            </Typography>
                                                        </Stack>
                                                    ))}
                                                </Stack>

                                                {/* Creator section */}
                                                {creatorName && (
                                                    <>
                                                        <Divider sx={{ borderColor: "#21262d" }} />

                                                        <Typography fontWeight={700} color="#e5e7eb" fontFamily={interFont} fontSize={14}>
                                                            Creator
                                                        </Typography>

                                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                                            <Avatar
                                                                src={creatorImg}
                                                                sx={{
                                                                    width: 40, height: 40,
                                                                    bgcolor: "#3b82f6", fontSize: 16, fontFamily: interFont,
                                                                }}
                                                            >
                                                                {creatorName.charAt(0).toUpperCase()}
                                                            </Avatar>
                                                            <Box>
                                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                    <Typography fontSize={14} fontWeight={600} color="#e5e7eb" fontFamily={interFont}>
                                                                        {creatorName}
                                                                    </Typography>
                                                                    <VerifiedTick style={{ width: 14, height: 14 }} />
                                                                </Stack>
                                                                {creatorEmail && (
                                                                    <Typography fontSize={12} color="#6b7280" fontFamily={interFont}>
                                                                        {creatorEmail}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </>
                                                )}

                                            </Stack>
                                        </CardContent>
                                    </Paper>
                                </Box>
                            </Grid>

                        </Grid>
                    )}
                </Box>
            </Box>
        </>
    );
}